import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    onDocumentReady(() => {
      this.#preloadImages();
      this.#bindMoreDropdown();
    });
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  activate = (event) => {
    this.#debouncedDeactivate.cancel();

    if (this.#state.activeItem) {
      this.#activateHandler(event);
    } else {
      this.#debouncedActivateHandler(event);
    }
  };

  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    this.dataset.overflowExpanded = (event.target.slot !== '').toString();

    this.#deactivate();
    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    const submenu = findSubmenu(item);

    if (!submenu) return;

    this.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate(event);
    }
  }

  #debouncedDeactivate = debounce((event) => {
    this.dataset.overflowExpanded = 'false';

    const item = findMenuItem(event.target);

    if (item != this.#state.activeItem) return;

    this.#deactivate(item);
  }, DEACTIVATE_DELAY);

  #deactivate = (item = this.#state.activeItem) => {
    this.style.setProperty('--submenu-height', '0px');

    if (!item) return;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Build and toggle the custom More dropdown
   */
  #bindMoreDropdown = () => {
    const moreToggle = this.querySelector('[data-more-toggle]');
    const dropdown = this.querySelector('[data-more-dropdown]');
    const list = dropdown?.querySelector('.more-dropdown__list');
    const overflowHost = this.querySelector('overflow-list');

    if (!moreToggle || !dropdown || !list || !overflowHost) return;

    // Populate items from overflow slot (shadow DOM)
    const overflowRoot = overflowHost.shadowRoot;
    const slot = overflowRoot?.querySelector('slot[name="overflow"]');
    const nodes = slot ? slot.assignedElements({ flatten: true }) : [];

    list.innerHTML = '';
    nodes.forEach((node) => {
      const link = node.querySelector('a');
      if (!link) return;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = link.href;
      a.textContent = link.textContent?.trim() || '';
      a.className = 'menu-list__link';
      li.appendChild(a);
      list.appendChild(li);
    });

    const open = () => {
      dropdown.hidden = false;
      document.addEventListener('click', onOutside, { once: true });
    };
    const close = () => {
      dropdown.hidden = true;
    };
    const onOutside = (e) => {
      if (!(e.target instanceof Element)) return close();
      if (!dropdown.contains(e.target) && !moreToggle.contains(e.target)) close();
    };

    // Click toggle (desktop/mobile)
    moreToggle.addEventListener('click', (e) => {
      e.preventDefault();
      dropdown.hidden ? open() : close();
    });

    // Open on hover (mouse only) and focus, with small close delay
    const container = moreToggle.closest('[slot="more"]');
    let hoverCloseTimer = null;

    const scheduleClose = () => {
      if (hoverCloseTimer) clearTimeout(hoverCloseTimer);
      hoverCloseTimer = setTimeout(() => close(), 200);
    };
    const cancelClose = () => {
      if (hoverCloseTimer) {
        clearTimeout(hoverCloseTimer);
        hoverCloseTimer = null;
      }
    };

    if (container) {
      container.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') {
          cancelClose();
          open();
        }
      });
      container.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') {
          // If moving into dropdown or toggle, don't close immediately
          const rt = /** @type {PointerEvent} */ (e).relatedTarget;
          if (rt instanceof Element && (container.contains(rt) || dropdown.contains(rt))) return;
          scheduleClose();
        }
      });

      // Keep open when hovering dropdown itself
      dropdown.addEventListener('pointerenter', () => {
        cancelClose();
        open();
      });
      dropdown.addEventListener('pointerleave', (e) => {
        const rt = /** @type {PointerEvent} */ (e).relatedTarget;
        if (rt instanceof Element && (container.contains(rt) || dropdown.contains(rt))) return;
        scheduleClose();
      });

      container.addEventListener('focusin', () => {
        cancelClose();
        open();
      });
      container.addEventListener('focusout', (e) => {
        const related = /** @type {FocusEvent} */ (e).relatedTarget;
        if (related instanceof Element && container.contains(related)) return;
        scheduleClose();
      });
    }
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[role="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
